/*
  MOT-01 — the behaviour behind snippets/media-toggle.liquid.

  One control for anything that moves on its own: CSS marquees, and autoplaying video
  that carries no controls of its own. WCAG 2.2.2 requires a mechanism once the movement
  runs past five seconds.

  It flips `motion-paused` on the element named by `data-target` (its own parent when the
  attribute is absent), pauses every <video> inside that element, and swaps the button's
  accessible name to the action that button will perform next.

  The displayed state follows the media rather than a local flag, so it stays honest when
  playback stops for a reason this control did not cause - a slide change, a background
  tab, a media error. `prefers-reduced-motion: reduce` starts it stopped.

  One control per region, not one per tile, wherever the tiles are links: a <button>
  cannot live inside an <a>.
*/
class MotionToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.target = this.dataset.target
      ? document.querySelector(this.dataset.target)
      : this.parentElement;

    if (!this.button || !this.target) return;

    this.videos = Array.from(this.target.querySelectorAll('video'));
    this.paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.button.addEventListener('click', () => {
      this.paused = !this.paused;
      this.applyToMedia();
      this.render();
    });

    this.videos.forEach((video) => {
      video.addEventListener('play', () => this.followMedia());
      video.addEventListener('pause', () => this.followMedia());
    });

    // Media is only touched on the first pass when the answer is "stop". Calling play()
    // on load would start videos the browser had its own reasons not to.
    if (this.paused) this.applyToMedia();
    this.render();
  }

  followMedia() {
    if (!this.videos.length) return;
    this.paused = this.videos.every((video) => video.paused);
    this.render();
  }

  applyToMedia() {
    this.videos.forEach((video) => {
      if (this.paused) {
        video.autoplay = false;
        video.pause();
      } else {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }
    });
  }

  render() {
    this.target.classList.toggle('motion-paused', this.paused);
    this.button.classList.toggle('media-toggle--paused', this.paused);
    this.button.setAttribute(
      'aria-label',
      this.paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause
    );
  }
}

if (!customElements.get('motion-toggle')) {
  customElements.define('motion-toggle', MotionToggle);
}
